import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share2, Copy, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { drawShareCard } from "@/lib/shareCard";

function useShareCard(refund) {
  const [dataUrl, setDataUrl] = useState("");
  const [art, setArt] = useState(null);
  const [artState, setArtState] = useState("loading"); // loading | ready | off
  const canvasEl = useRef(null);

  const paint = useCallback((canvas, image) => {
    try {
      setDataUrl(drawShareCard(canvas, refund, image));
    } catch {
      toast.error("Couldn't render the share card");
    }
  }, [refund]);

  // Ref callback draws as soon as the canvas is attached to DOM (avoids Radix Portal timing issues)
  const canvasRef = useCallback((canvas) => {
    if (!canvas) return;
    canvasEl.current = canvas;
    paint(canvas, art);
  }, [paint, art]);

  const fetchArt = useCallback(async (refresh = false) => {
    setArtState("loading");
    try {
      const { data } = await api.post(`/refunds/${refund.id}/share-art${refresh ? "?refresh=true" : ""}`);
      const img = new Image();
      img.onload = () => { setArt(img); setArtState("ready"); if (canvasEl.current) paint(canvasEl.current, img); };
      img.onerror = () => setArtState("off");
      img.src = `data:image/png;base64,${data.image_base64}`;
      if (refresh) toast.success("New artwork generated");
    } catch (e) {
      setArtState("off");
      if (e?.response?.status !== 402) toast.message("Using the classic card design", { description: "AI artwork wasn't available right now." });
    }
  }, [refund.id, paint]);

  useEffect(() => { fetchArt(); }, [fetchArt]);

  const download = useCallback(() => {
    if (!dataUrl) { toast.error("Card not ready yet"); return; }
    const a = document.createElement("a");
    a.download = `clawback-${refund.merchant.replace(/\s+/g, "_")}-${refund.amount.toFixed(2)}.png`;
    a.href = dataUrl; a.click();
    toast.success("Share card downloaded");
  }, [dataUrl, refund]);

  const nativeShare = useCallback(async () => {
    if (!dataUrl) { toast.error("Card not ready yet"); return; }
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "clawback.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `I clawed back $${refund.amount.toFixed(2)}`, text: `Clawback got me ${refund.merchant} back — no bank password.` });
        return;
      }
    } catch { /* fall through to download */ }
    download();
  }, [dataUrl, refund, download]);

  return { dataUrl, canvasRef, download, nativeShare, artState, fetchArt };
}

export default function ShareCardModal({ refund, onClose }) {
  const [copied, setCopied] = useState(false);
  const { dataUrl, canvasRef, download, nativeShare, artState, fetchArt } = useShareCard(refund);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Link copied");
    } catch {
      toast.error(`Clipboard blocked — link: ${window.location.origin}`);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#141618] border-[#282C30] text-[#F4F1EA] max-w-lg" data-testid="share-card-modal">
        <DialogHeader>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">SHARE YOUR WIN</div>
          <DialogTitle className="font-display text-2xl tracking-tight">${refund.amount.toFixed(2)} from {refund.merchant}</DialogTitle>
          <DialogDescription className="text-[#A0A7B0]">
            Post to TikTok / IG — show off the win and pull friends in.
          </DialogDescription>
        </DialogHeader>
        <canvas ref={canvasRef} width={1080} height={1080} style={{ display: "none" }} data-testid="share-card-canvas" aria-hidden="true" />
        <div className="rounded-md overflow-hidden border border-[#282C30] bg-[#0C0D0E] min-h-[200px] grid place-items-center relative">
          {dataUrl ? (
            <img src={dataUrl} alt="share preview" className="w-full h-auto" data-testid="share-card-preview" />
          ) : (
            <div className="py-16 text-sm text-[#606872] font-mono uppercase tracking-widest">Rendering…</div>
          )}
          {artState === "loading" && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-sm bg-[#0C0D0E]/85 border border-[#282C30] px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#00E676]" data-testid="share-art-loading">
              <Sparkles className="h-3 w-3 animate-pulse" /> Painting your win with AI…
            </div>
          )}
          {artState === "ready" && (
            <button onClick={() => fetchArt(true)} className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-sm bg-[#0C0D0E]/85 border border-[#282C30] px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-[#A0A7B0] hover:text-[#F4F1EA]" data-testid="share-art-regenerate">
              <Sparkles className="h-3 w-3" /> New artwork
            </button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <Button variant="outline" onClick={copyLink} className="btn-outline-paper" data-testid="share-copy-link-button">
            {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />} {copied ? "Copied" : "Link"}
          </Button>
          <Button variant="outline" onClick={download} className="btn-outline-paper" data-testid="share-download-button" disabled={!dataUrl}>
            <Download className="h-4 w-4 mr-1" /> PNG
          </Button>
          <Button className="btn-emerald" onClick={nativeShare} data-testid="share-native-button" disabled={!dataUrl}>
            <Share2 className="h-4 w-4 mr-1" /> Share
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
